import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import client from "../../api/client";
import { GlobalContext } from "../../context";
import { getCurrentUser } from "../../context/actions/auth";
import Logo from "../../components/AllVon/VonHeader/assets/logo.png";

function Field({ label, type = "text", value, onChange, placeholder, required, autoComplete }) {
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  return (
    <div>
      <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className="relative">
        <input
          type={isPassword && show ? "text" : type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          autoComplete={autoComplete}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
            focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 focus:border-[#294a93]
            transition-colors bg-white"
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            tabIndex={-1}
          >
            {show ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default function InstructorAcceptInvite() {
  const { token }     = useParams();
  const navigate      = useNavigate();
  const { authDispatch } = useContext(GlobalContext);

  const [status,   setStatus]   = useState("loading"); // loading | valid | error | done
  const [errorMsg, setErrorMsg] = useState("");
  const [email,    setEmail]    = useState("");

  const [form, setForm] = useState({
    firstname:             "",
    lastname:              "",
    telephone:             "",
    password:              "",
    password_confirmation: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    client.get(`/auth/instructor/invite/${token}`)
      .then((res) => {
        setEmail(res.data.email);
        setStatus("valid");
      })
      .catch((err) => {
        setErrorMsg(err?.response?.data?.message || "This invitation link is invalid or has expired.");
        setStatus("error");
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.password !== form.password_confirmation) {
      toast.error("Passwords do not match.");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        token,
        password:              form.password,
        password_confirmation: form.password_confirmation,
        firstname:             form.firstname,
        lastname:              form.lastname,
        telephone:             form.telephone,
      };

      const res = await client.post("/auth/instructor/accept-invite", payload);
      const { token: apiToken } = res.data;

      // Store credentials exactly like the normal sign-in flow
      localStorage.setItem("nd-rest-tkn", apiToken);
      client.defaults.headers.Authorization = `Bearer ${apiToken}`;
      client.defaults.headers.post["Content-Type"] = "application/json";

      await getCurrentUser(authDispatch);

      toast.success("Welcome aboard! Your instructor account is ready.");
      setStatus("done");
      setTimeout(() => navigate("/dashboard"), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  // ── Loading ──
  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-gray-200 border-t-[#294a93] rounded-full animate-spin" />
      </div>
    );
  }

  // ── Error ──
  if (status === "error") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 max-w-md w-full text-center">
          <img src={Logo} alt="E1X Academy" className="h-12 w-auto mx-auto mb-6 object-contain" />
          <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-2">Invitation Invalid</h2>
          <p className="text-sm text-gray-500 mb-6">{errorMsg}</p>
          <a href="mailto:support@edoubleone.net" className="text-sm text-[#294a93] hover:underline font-semibold">
            Contact support →
          </a>
        </div>
      </div>
    );
  }

  // ── Success / redirecting ──
  if (status === "done") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 max-w-md w-full text-center">
          <img src={Logo} alt="E1X Academy" className="h-12 w-auto mx-auto mb-6 object-contain" />
          <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-1">You're all set!</h2>
          <p className="text-sm text-gray-500">Redirecting you to your dashboard…</p>
        </div>
      </div>
    );
  }

  // ── Valid — show form ──
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm w-full max-w-md">

        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-gray-100 text-center">
          <img src={Logo} alt="E1X Academy" className="h-12 w-auto mx-auto mb-5 object-contain" />
          <h1 className="text-xl font-bold text-gray-900">Accept Instructor Invitation</h1>
          <p className="text-sm text-gray-500 mt-1">
            You've been invited to join <span className="font-semibold text-[#001931]">E1X Academy</span> as an instructor.
          </p>
          <div className="mt-3 inline-flex items-center gap-2 bg-[#294a93]/8 px-3 py-1.5 rounded-full">
            <svg className="w-3.5 h-3.5 text-[#294a93]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-xs font-semibold text-[#294a93]">{email}</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">

          {/* Name fields — always shown */}
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="First Name" required
              value={form.firstname} onChange={set("firstname")}
              placeholder="Ada" autoComplete="given-name"
            />
            <Field
              label="Last Name" required
              value={form.lastname} onChange={set("lastname")}
              placeholder="Lovelace" autoComplete="family-name"
            />
          </div>
          <Field
            label="Phone Number"
            value={form.telephone} onChange={set("telephone")}
            placeholder="+234 800 000 0000" autoComplete="tel"
          />

          <Field
            label="Password" type="password" required
            value={form.password} onChange={set("password")}
            placeholder="At least 8 characters" autoComplete="new-password"
          />
          <Field
            label="Confirm Password" type="password" required
            value={form.password_confirmation} onChange={set("password_confirmation")}
            placeholder="Repeat your password" autoComplete="new-password"
          />

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#294a93] hover:bg-[#1e3a7a] disabled:opacity-60
              text-white font-bold py-3 rounded-xl text-sm transition-colors mt-2"
          >
            {submitting ? "Setting up your account…" : "Activate Instructor Account"}
          </button>

          <p className="text-center text-xs text-gray-400">
            Already have an account?{" "}
            <a href="/sign-in" className="text-[#294a93] font-semibold hover:underline">Sign in</a>
          </p>
        </form>
      </div>
    </div>
  );
}
